import { Flex, Select } from "@mantine/core";
import { pickBy } from "lodash";
import { DataTableSortStatus } from "mantine-datatable";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useInvoices } from "../../../api/hooks/invoice/useInvoices.ts";
import { getInvoiceApiSort } from "../../../components/Invoice/InvoicesTable/utils.ts";
import SectionTitle from "../../../components/Section/SectionTitle/SectionTitle.tsx";
import Table from "../../../components/Table/Table.tsx";
import { SortDirection, TableRow } from "../../../components/Table/types.ts";
import { AppRoute } from "../../../constants.ts";
import {
  DateTimeFilter,
  InvoiceFilter,
  InvoiceOrderBy,
  OrderBy,
  PaymentStatusFilterOptions,
  PaymentType,
} from "../../../graphql/generated";
import { Invoice } from "../../../types/invoice/invoice.ts";
import { getPagesCount } from "../../../utils/pagination.ts";
import { CollectionDashboardFilter } from "../types.ts";
import { nextDueOptions } from "./constants.ts";
import { useStyles } from "./CustomersNextDue.styles.ts";
import { useCustomersNextDueTableData } from "./hooks/useCustomersNextDueTableData.tsx";
import DurationConstructor = moment.unitOfTime.DurationConstructor;

interface CustomersNextDueProps {
  filter: CollectionDashboardFilter;
}

const CustomersNextDue = ({ filter }: CustomersNextDueProps) => {
  const navigate = useNavigate();
  const { classes } = useStyles();

  const [orderBy, setOrderBy] = useState<InvoiceOrderBy>({
    invoiceDueDate: OrderBy.Desc,
  });
  const [page, setPage] = useState<number>(1);

  const [dueDate, setDueDate] = useState<string | null>();

  const dueDateFilter = useMemo((): DateTimeFilter | null => {
    const today = moment.utc().startOf("day");

    if (!dueDate) {
      return { gte: today.toDate() };
    }

    const [amount, unit] = dueDate.split("-");
    const date = moment
      .utc()
      .startOf("day")
      .add(amount, unit as DurationConstructor);
    return { lte: date.toDate(), gte: today.toDate() };
  }, [dueDate]);

  const invoiceFilter: InvoiceFilter = pickBy({
    invoiceDueDate: dueDateFilter,
    salesTeamName: filter.salesTeam?.length && { in: filter.salesTeam },
    paymentType: { equals: PaymentType.Top },
    paymentStatus: {
      notIn: [
        PaymentStatusFilterOptions.Paid,
        PaymentStatusFilterOptions.PaidWithLateFee,
      ],
    },
  });

  const [{ data: invoicesData, fetching }] = useInvoices({
    filter: invoiceFilter,
    orderBy,
    page,
  });
  const invoices = invoicesData?.invoices?.data;
  const pageCount = getPagesCount(invoicesData?.invoices?.total);

  const [columns, rows] = useCustomersNextDueTableData({
    data: invoices,
  });

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getInvoiceApiSort(sort));
    },
    [setOrderBy]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleDueDateChange = useCallback((value: string | null) => {
    setDueDate(value);
    setPage(1);
  }, []);

  const handleInvoiceClick = (record: TableRow) => {
    if (!invoices) return;
    const invoice = invoices.find((item: Invoice) => item.id === record.id);
    if (!invoice) return;
    navigate(`${AppRoute.ActivityManagement}/${invoice.customer.id}`);
  };

  return (
    <Flex direction="column" gap={20}>
      <SectionTitle>List of Customers Next Due</SectionTitle>
      <Flex maw="584px">
        <Select
          label="Due in the next"
          placeholder="Select Value"
          data={nextDueOptions}
          value={dueDate}
          className={classes.nextDueFilter}
          size="m"
          clearable
          onChange={handleDueDateChange}
        />
      </Flex>
      <Table
        loading={fetching}
        minWidth="100%"
        columns={columns}
        rows={rows}
        defaultSort={{
          columnAccessor: "invoiceDueDate",
          direction: SortDirection.desc,
        }}
        pagination={{
          pageCount: pageCount,
          page: page,
          onPageChange: handlePageChange,
        }}
        onSortChange={handleSortChange}
        onRowClick={handleInvoiceClick}
      />
    </Flex>
  );
};

export default CustomersNextDue;
